import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, margin } from "@mui/system";
import {
  BLOCKED_CONTACTS_LIST,
  CAMPAIGN_CREATE_DAYS_LIST,
  EMITTER_ERROR,
  EMITTER_SUCCESS,
  EMITTER_WARNING,
  initialTimings,
  isOAuthMailboxEsp,
  TIMEZONE_LIST,
} from "../../../../utils/constants";
import classes1 from "../../../../components/TimeScheduler/TimeScheduler.module.css";
import { getRequest, postRequest } from "../../../../Apis";
import { useNavigate, useParams } from "react-router-dom";
import {
  authEndpoints,
  campaignEndpoints,
  CampaignSettingsEndpoints,
} from "../../../../utils/constants/httpConstants";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { isDemoTenant } from "../../../../utils/helpers/tenantConfig";
import {
  isRestrictedTenant,
  isTenantPromptOnlyTenant,
} from "../../../../utils/helpers/tenantRestrictions";
import { useDispatch } from "react-redux";
import {
  dateValidation,
  nameValidation,
} from "../../../../utils/validations/inputValidations";
import ErrorMessage from "../../../../components/Messages/ErrorMessage";
import {
  createTimingsForPayload,
  createTimingsFromPayload,
  findSortedValue,
  getTimeStampForTimeZone,
  handleGoogleAuth,
  handleMicrosoftAuthInitialize,
  handleZohoAuth,
  offsetForTimezoneOnDate,
} from "../../../../utils/helpers";
import Stepper from "@mui/material/Stepper";
import SkippedContact from "./SkippedContact";
import { Button } from "../../../../components/Buttons";
import CustomModal from "../../../../components/Modals/CustomModal";
import CustomTooltip from "../../../../components/Tooltip/CustomTooltip";
import {
  ACTIVE_IMG,
  DONE_IMG,
  GOOGLE_ICON,
  MICROSOFT_IMAGE,
  NO_RECORD_FOUND_IMAGE,
  UNSELECTED_IMG,
  ZOHO_ICON,
} from "../../../../utils/constants/assets";
import { ICON_LABELS, fontWeight } from "../../../../utils/constants/UI";
import { BASE, BORDER, BRAND, GREY } from "../../../../utils/constants/colors";
import InputField from "../../../../components/InputFields/InputField";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import Image from "../../../../components/Image/Image";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import EspSkeleton from "../common/EspSkeleton";
import CustomDatePicker from "../../../../components/DatePicker/CustomDatePicker";
import { paginationDataAction } from "../../../../redux/actions/customTableActions";
import { updateAvailableCredits } from "../../../../redux/actions/authActions";
import {
  MESSAGE_ALL_FIELD_REQUIRED,
  SERVER_MESSAGE_ABSENCE,
  SERVER_MESSAGE_MISPLACED,
} from "../../../../utils/constants/messages";
import classes from "../markComponents.module.css";
import { STRINGS } from "../strings";
import TimeScheduler from "../../../../components/TimeScheduler/TimeScheduler";
import RadioButton from "../../../../components/RadioButton/RadioButton";
import { IconComponent } from "../../../../components/Icon";
import BasicDetailsStep from "./CreateCampaignSteps/BasicDetailsStep";
import SelectSequenceStep from "./CreateCampaignSteps/SelectSequenceStep";
import CustomizationKeysStep from "./CreateCampaignSteps/CustomizationKeysStep";
import SkippedContactsStep from "./CreateCampaignSteps/SkippedContactsStep";
import ScheduleTimingsStep from "./CreateCampaignSteps/ScheduleTimingsStep";
import CampaignPreviewStep from "./CreateCampaignSteps/CampaignPreviewStep";
import { useHandleApi } from "../../../../hooks/useHandleApi";

// "first_name" -> "First Name". Only used when the keys endpoint sends a key
// with no label of its own.
const humanizeKey = (key) =>
  String(key || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

// list/contactlist-fields returns its rows grouped by bucket:
//   data: { people: [{key, tag, label, sample_value}], company: [...] }
// Flatten that into one {key, label, group} list, keeping the bucket as `group`
// so the step can tab by it. sample_value is deliberately dropped - the step
// lists keys only. A bare array is accepted too (it just lands ungrouped), and
// duplicate keys are dropped so two rows can never share a React key.
const normaliseListKeys = (data) => {
  const buckets = Array.isArray(data)
    ? [["", data]]
    : Object.entries(data || {}).filter(([, rows]) => Array.isArray(rows));

  const seen = new Set();
  const result = [];
  buckets.forEach(([group, rows]) => {
    rows.forEach((row) => {
      const item = typeof row === "string" ? { key: row } : row || {};
      const key = item.key || item.field_name || item.id || "";
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push({
        key,
        label: item.label || item.field_name || humanizeKey(key),
        group: item.group || group || "",
      });
    });
  });
  return result;
};

const millisecondsToDateStringWithoutNumber = (date) => {
  const schedule_date = new Date(date);
  const result = `${schedule_date.getFullYear()}-${
    schedule_date.getMonth() <= 9 ? "0" : ""
  }${schedule_date.getMonth() + 1}-${
    schedule_date.getDate() <= 9 ? "0" : ""
  }${schedule_date.getDate()}`;
  return result;
};

const CreateCampaignModal = ({
  open,
  setOpen,
  nextstep,
  setPayload = () => {},
  payload,
  ...props
}) => {
  const { id } = useParams();
  const date = new Date();
  const currentDate = `${date.getFullYear()}-${
    date.getMonth() + 1 <= 9 ? "0" : ""
  }${date.getMonth() + 1}-${date.getDate() <= 9 ? "0" : ""}${date.getDate()}`;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sequence, setSequence] = useState(
    payload?.sequence_id ? [payload.sequence_id] : [],
  );
  const [name, setName] = useState(
    payload?.name ? STRINGS.COPYOF + " " + payload?.name : "",
  );
  const [scheduleDate, setScheduleDate] = useState(null);
  let offset1 = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezone_objChange = TIMEZONE_LIST?.filter(
    (item) => item?.id === offset1,
  );
  const [timezone, setTimeZone] = useState([
    payload?.timezone || timezone_objChange?.[0]?.id,
  ]);
  const [sequenceList, setSequenceList] = useState([]);
  const [selectedList, setSelectedList] = useState(
    payload?.list_id ? [payload.list_id] : [],
  );
  const [contactList, setContactList] = useState([]);
  const [espList, setEspList] = useState([]);
  const [signState, setSignState] = useState([]);
  const [idSignState, setIdSignState] = useState([]);
  const [accountIdByEmail, setAccountIdByEmail] = useState({});
  const [mailList, setMailList] = useState([]);
  const [itemData, setItemData] = useState({});
  const [outList, setOutList] = useState([]);
  const [zohoList, setZohoList] = useState([]);
  // Individual mode's manual sender picks. One flat, order-preserving list
  // (not per-provider buckets) so a mix of e.g. a Gmail + a Zoho mailbox is
  // natively representable, and "first selected" is just entries[0].
  const [standaloneSenders, setStandaloneSenders] = useState([]);
  const [isEnableList, setIsEnableList] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  // Send Via is multi-select: none, one, or several connected ESPs at once.
  const [selectedEsps, setSelectedEsps] = useState(
    payload?.sender_esp ? [payload.sender_esp] : [],
  );
  const [getTimings, setGetTimings] = useState(
    createTimingsFromPayload(payload?.timings) || [...initialTimings],
  );
  const [timezoneList, setTimezoneList] = useState([...TIMEZONE_LIST]);
  const [sequenceType, setSequenceType] = useState(
    isDemoTenant() || isRestrictedTenant() || isTenantPromptOnlyTenant()
      ? "tenantPrompt"
      : "automated",
  );
  const [editTiming, setEditTiming] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [blockedList, setBlockedList] = useState([]);
  const [sequenceLabel, setSequenceLabel] = useState("");
  const [listLabel, setListLabel] = useState("");
  const [validations, setValidations] = useState({
    name: "",
    scheduleDate: "",
  });
  const [sendVia, setSendVia] = useState("");
  const [icpPersonaEnabled, setIcpPersonaEnabled] = useState(false);
  // Create-time-only email options. Both ON = today's behaviour, and ON sends
  // no key at all, so an untouched create request is byte-identical to before.
  // Deliberately NOT seeded from `payload` (the clone path): on a campaign row
  // `unsubscribe` is an integer stat COUNT, not a setting, so there is nothing
  // meaningful to inherit - a clone always starts from the defaults.
  const [unsubscribeEnabled, setUnsubscribeEnabled] = useState(true);
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [companyPersonas, setCompanyPersonas] = useState([]);
  const [personPersonas, setPersonPersonas] = useState([]);
  const [companyPersonaId, setCompanyPersonaId] = useState([]);
  const [companyPersonaLabel, setCompanyPersonaLabel] = useState("");
  const [personPersonaId, setPersonPersonaId] = useState([]);
  const [personPersonaLabel, setPersonPersonaLabel] = useState("");
  const [personasLoading, setPersonasLoading] = useState(false);

  // ─── Customization keys ─────────────────────────────────────────────
  // The {{tags}} this campaign can personalise with. They belong to the
  // CONTACT LIST picked on the previous step, so they are fetched per list_id
  // from /campaign/list-keys - there is no static fallback set.
  const [listKeys, setListKeys] = useState([]);
  const [listKeysLoading, setListKeysLoading] = useState(false);
  const [listKeysError, setListKeysError] = useState("");
  // total_contacts off the same response - the sample values shown next to each
  // key are drawn from these contacts, so it is worth stating how many.
  const [listKeysContactCount, setListKeysContactCount] = useState(null);
  const [selectedCustomizationKeys, setSelectedCustomizationKeys] = useState(
    [],
  );
  // Which list the cached keys belong to. Re-entering the step for the same
  // list must not refetch, and must not stomp the user's manual selection;
  // switching to a different list must do both.
  const loadedKeysListIdRef = React.useRef("");
  // Bumped by the Try again action to re-run the fetch effect after a failure.
  const [listKeysRetryToken, setListKeysRetryToken] = useState(0);

  // One signature per selected sender email. signatureListByEmail caches the
  // mailbox-scoped /signature/list response so re-checking an email is instant.
  const [signaturesByEmail, setSignaturesByEmail] = useState({});
  const [signatureListByEmail, setSignatureListByEmail] = useState({});
  const [signatureLoadingByEmail, setSignatureLoadingByEmail] = useState({});
  // In-flight /signature/list promises keyed by email, so the lazy prefetch
  // effect and the Next-button gate share one request instead of double-hitting.
  const signaturePromisesByEmail = React.useRef({});

  // Mailbox groups. Step 1 exposes two radios - "group" and "standalone"
  // (individual mailboxes) - and senderMode decides which data source drives
  // the picker. The individual-mailbox API is only fetched in standalone mode,
  // so an empty groups list does NOT silently fall back to / call it.
  const [senderMode, setSenderMode] = useState("group");
  // Once we know whether groups exist, pick a sensible default the FIRST time
  // only - never stomp a mode the user has already chosen by hand. A ref (not
  // state) so flipping it doesn't trigger an extra render.
  const senderModeAutoSetRef = React.useRef(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  // Fallback esp-by-email lookup sourced from group/list's members[].esp, in
  // case the per-group /emails detail endpoint doesn't (yet) echo esp back.
  const [groupMemberEspByEmail, setGroupMemberEspByEmail] = useState({});
  const [groupMembersById, setGroupMembersById] = useState({});
  const [groupMembersLoadingById, setGroupMembersLoadingById] = useState({});
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  // Per-group set of emails the user has explicitly unticked inside View More.
  // Those are removed from the effective sender list even though the group is on.
  const [excludedEmailsByGroupId, setExcludedEmailsByGroupId] = useState({});

  const { loading: creating, handleApi: handleCreateApi } = useHandleApi();
  const { loading: creating1, handleApi: handleScheduleApi } = useHandleApi();
  const { loading: stepActivity, handleApi: handleStepActivityApi } =
    useHandleApi();
  const { loading: isEligibleLoad, handleApi: handleEligibleApi } =
    useHandleApi();
  const { loading: initialLoading, handleApi: handleInitialLoadingApi } =
    useHandleApi();

  // Tour-only: step jump and abort close via custom DOM events.
  // These listeners are purely additive and only fire when the tour dispatches them.
  useEffect(() => {
    const onSetStep = (e) => setActiveStep(e.detail.step);
    const onClose = () => { setOpen(false); setPayload(false); };
    const onAborted = (e) => {
      if (e.detail?.key === "campaigns") { setOpen(false); setPayload(false); }
    };
    window.addEventListener("__tour:campaignSetStep", onSetStep);
    window.addEventListener("__tour:campaignCloseModal", onClose);
    window.addEventListener("__tour:aborted", onAborted);
    return () => {
      window.removeEventListener("__tour:campaignSetStep", onSetStep);
      window.removeEventListener("__tour:campaignCloseModal", onClose);
      window.removeEventListener("__tour:aborted", onAborted);
    };
  }, [setOpen, setPayload]);

  // Static reference data - memoize so it doesn't hand a fresh array identity
  // to the memoized step components on every parent re-render.
  const espConnectionList = useMemo(
    () => [
      {
        id: 1,
        label: STRINGS.CONNECT_GOOGLE,
        icon: GOOGLE_ICON,
      },
      {
        id: 2,
        label: STRINGS.CONNECT_OUTLOOK,
        icon: MICROSOFT_IMAGE,
      },
      {
        id: 3,
        label: STRINGS.CONNECT_ZOHO,
        icon: ZOHO_ICON,
      },
    ],
    [],
  );
  const [error, setError] = useState(false);

  const handleCreateCampaign = async (startNow = false) => {
    if (startNow) {
      setScheduleDate(currentDate);
    }
    const validatedName = nameValidation(name);
    const scheduledDate = startNow
      ? currentDate
      : millisecondsToDateStringWithoutNumber(scheduleDate);
    const validatedDate = dateValidation(scheduledDate);
    if (validatedName.isValid && validatedDate.isValid) {
      setValidations({ name: "", scheduleDate: "" });
      const dateArray = scheduledDate?.split("-");
      // Compute offset for the *selected* tz on the *scheduled* date - DST-aware.
      // TIMEZONE_LIST has frozen-in-time offsets that are wrong half the year.
      const scheduledDateObj = new Date(
        parseInt(dateArray[2]),
        parseInt(dateArray[1]) - 1,
        parseInt(dateArray[0]),
      );
      const offset = offsetForTimezoneOnDate(timezone?.[0], scheduledDateObj);
      const actualTimings = createTimingsForPayload(getTimings);
      const newScheduleDate = `${parseInt(dateArray[2])}-${parseInt(
        dateArray[1],
      )}-${parseInt(dateArray[0])}`;
      // from_email/sender_esp = whichever sender was selected FIRST (selection
      // order, not "the esp card"). from_emails carries each sender's OWN esp,
      // read straight off selectedSenderInfos - never a single shared value,
      // so a mixed Gmail+Zoho pick always tags each entry correctly.
      const emailVal = selectedSenderInfos?.[0]?.email || "";
      const senderEspVal =
        selectedSenderInfos?.[0]?.esp || Number(selectedEsps?.[0]) || 0;

      const fromEmails = selectedSenderInfos.map((s) => ({
        email: s.email,
        esp: Number(s.esp),
        signature_id: signaturesByEmail[s.email]?.id || null,
      }));

      const payloadObj = {
        name: name,
        sequenceType: sequenceType,
        list_id: selectedList?.[0] || "",
        from_email: emailVal,
        from_emails: fromEmails,
        sender_esp: senderEspVal,
        // Keys the user kept on in the Customization Keys step, as bare tag
        // names (no braces) - e.g. ["first_name", "company_name"].
        customization_keys: selectedCustomizationKeys,
      };

      if (sequenceType === "tenantPrompt") {
        payloadObj.prompt_id = sequence?.[0] || "";
      } else {
        payloadObj.sequence_id = sequence?.[0] || "";
      }

      if (icpPersonaEnabled) {
        payloadObj.isIcpCampaign = true;
        payloadObj.company_persona_id = companyPersonaId?.[0] || "";
        payloadObj.person_persona_id = "";
      }

      if (sequenceType !== "automated" && sequenceType !== "tenantPrompt") {
        payloadObj.scheduled_date = newScheduleDate;
        payloadObj.timezone = timezone?.[0];
        payloadObj.timings = actualTimings;
        payloadObj.offset = offset;
        if (startNow) {
          payloadObj.schedule_now = true;
        }
      }

      if (id) {
        payloadObj.parent = id;
      }

      idSignState?.[0]?.length > 0 &&
        (payloadObj.other_signature_id = idSignState[0]);

      // Email options are OPT-OUT keys: send a real boolean false only when the
      // user turned the option off. Leaving a key out is what keeps the current
      // behaviour, so an ON toggle must add nothing to the payload.
      if (trackingEnabled === false) {
        payloadObj.tracking = false;
      }
      if (unsubscribeEnabled === false) {
        payloadObj.unsubscribe = false;
      }

      const apiCall = () =>
        postRequest(navigate, campaignEndpoints.createCampaign, payloadObj);
      const apiOptions = {
        onSuccess: (response) => {
          dispatch(paginationDataAction({ query: {}, checkedRow: [] }));
          dispatch(updateAvailableCredits(navigate));
          setOpen(false);
          setPayload({});
          if (icpPersonaEnabled && response?.data?.data?.campaign_id) {
            navigate(
              `/user/campaigns/${response.data.data.campaign_id}/icp-review`,
            );
          }
        },
        showSuccess: true,
        showError: true,
        handleFinally: () => {
          setOpen(false);
          setPayload({});
        },
      };

      if (startNow) {
        handleCreateApi(apiCall, apiOptions);
      } else {
        handleScheduleApi(apiCall, apiOptions);
      }
    } else {
      setValidations({
        name: validatedName.message,
        scheduleDate: EventEmitter.emit(EMITTER_ERROR, validatedDate.message),
      });
    }
  };

  const convertData = useCallback((val) => {
    const data = TIMEZONE_LIST.filter((value) => {
      return value?.id === val;
    });
    return data?.[0]?.label;
  }, []);

  const isNextStepDisabled = () => {
    if (
      !steps[activeStep]?.requiredFields?.filter((item) => !item?.length)
        ?.length
    ) {
      return false;
    }
    return true;
  };

  const handleSequenceTypeChange = useCallback(async (e) => {
    const newType = e.target.value;
    setSequenceType(newType); // Update state

    // Optional: Clear selected sequence when switching types if you want
    // setSequence([]);
    // setSequenceLabel("");

    try {
      let sequences = [];
      if (newType === "tenantPrompt") {
        const response = await postRequest(
          navigate,
          campaignEndpoints.tenantPrompts,
          {},
        );
        sequences = response?.data?.data?.map(function (item) {
          return { id: item._id, label: item.title };
        });
      } else {
        const response = await postRequest(
          navigate,
          campaignEndpoints.sequenceList,
          { sequenceType: newType },
        );
        sequences = response?.data?.data?.map(function (item) {
          return { id: item._id, label: item.name };
        });
      }

      setSequenceList(sequences);

      // Set the first item as selected automatically if list is not empty
      if (sequences?.length > 0) {
        setSequence([sequences?.[0]?.id]);
        setSequenceLabel(sequences?.[0]?.label);
        if (contactList?.length > 0) {
          setIsEnableList(false);
        }
      } else {
        setSequence([]);
        setSequenceLabel("");
        setIsEnableList(true);
      }
    } catch (error) {
      console.error("Error fetching sequences", error);
    }
  }, [navigate, contactList]);

  // Fetch signature lists for any selected sender emails not yet cached and
  // return the emails that end up with NO signature. Used by the Basic Details
  // check so it stays correct even if the lazy per-email effect hasn't run yet.
  const resolveSendersMissingSignature = async () => {
    const emails = selectedSenderEmails.filter(Boolean);
    const results = await Promise.all(
      emails.map(async (email) => {
        let list = signatureListByEmail[email];
        if (!Array.isArray(list)) {
          list = await fetchSignatureListForEmail(email);
        }
        return { email, count: Array.isArray(list) ? list.length : 0 };
      }),
    );
    return results.filter((r) => r.count === 0).map((r) => r.email);
  };

  // Take signature-less mailboxes out of the sender selection instead of
  // blocking on them. Individual mode just filters its own picks; group mode
  // marks the email excluded on every selected group that contains it, which
  // is exactly what unticking it inside View More does - so the group stays
  // selected and only that member drops out.
  const dropSendersWithoutSignature = (emails) => {
    if (!emails?.length) return;
    const drop = new Set(emails);
    setStandaloneSenders((prev) => prev.filter((s) => !drop.has(s.email)));
    setExcludedEmailsByGroupId((prev) => {
      const next = { ...prev };
      selectedGroupIds.forEach((groupId) => {
        const hits = (groupMembersById[groupId] || []).filter(
          (member) => member?.email && drop.has(member.email),
        );
        if (!hits.length) return;
        const excluded = new Set(next[groupId] || new Set());
        hits.forEach((member) => excluded.add(member.email));
        next[groupId] = excluded;
      });
      return next;
    });
  };

  const handleStepAddition = async () => {
    try {
      const currentStep = steps?.[activeStep];

      if (currentStep.label === STRINGS.CAMP_BASIC_DETAILS) {
        // Mailboxes with no signature are dropped from the senders rather than
        // holding the step: the campaign simply won't send from them. Only a
        // selection that would be left with NOTHING still stops here, since
        // that would create a campaign with no sender at all.
        // Force-resolve uncached emails so the check is reliable.
        const missing = await resolveSendersMissingSignature();
        if (missing.length > 0) {
          if (missing.length >= selectedSenderEmails.length) {
            EventEmitter.emit(
              EMITTER_ERROR,
              STRINGS.CAMP_SIGNATURE_ALL_MISSING,
            );
            return;
          }
          dropSendersWithoutSignature(missing);
          EventEmitter.emit(
            EMITTER_WARNING,
            missing.length === 1
              ? `${missing[0]} ${STRINGS.CAMP_SIGNATURE_DROPPED_ONE}`
              : `${missing.length} ${STRINGS.CAMP_SIGNATURE_DROPPED_MANY}`,
          );
        }
        const nameValidated = nameValidation(name);
        if (nameValidated?.isValid) {
          handleStepActivityApi(
            async () => {
              const isCampExist = await postRequest(
                navigate,
                campaignEndpoints.isCampExist,
                { name: name },
              );
              if (isCampExist.status === 200) {
                setValidations({ name: "", scheduleDate: "" });
                let sequences = [];
                if (sequenceType === "tenantPrompt") {
                  const sequenceResponse = await postRequest(
                    navigate,
                    campaignEndpoints.tenantPrompts,
                    {},
                  );
                  sequences = sequenceResponse?.data?.data?.map(
                    function (item) {
                      return { id: item._id, label: item.title };
                    },
                  );
                } else {
                  const sequenceResponse = await postRequest(
                    navigate,
                    campaignEndpoints.sequenceList,
                    { sequenceType: sequenceType },
                  );
                  sequences = sequenceResponse?.data?.data?.map(
                    function (item) {
                      return { id: item._id, label: item.name };
                    },
                  );
                }
                const listResponse = await getRequest(
                  navigate,
                  campaignEndpoints.contactlist,
                );
                const contactsLists = listResponse?.data?.data?.map(
                  function (item) {
                    return { id: item._id, label: item.list_name };
                  },
                );

                // Nothing to pick on the Sequence & List step. This gates THAT
                // step's Next only (see isSequenceListStep below) - Basic
                // Details validates the campaign name, ESP and senders, and
                // must never be blocked by it.
                if (!sequences?.length || !contactsLists?.length) {
                  setIsEnableList(true);
                } else {
                  setIsEnableList(false);
                }
                if (!payload?.sequence_id) {
                  setSequence(sequences?.[0]?.id ? [sequences[0].id] : []);
                  setSequenceLabel(sequences?.[0]?.label || "");
                } else {
                  const value = sequences?.filter(
                    (item) => item?.id === payload.sequence_id,
                  );
                  setSequence(value?.[0]?.id ? [value[0].id] : []);
                  setSequenceLabel(value?.[0]?.label || "");
                }
                if (!payload?.list_id) {
                  setSelectedList(
                    contactsLists?.[0]?.id ? [contactsLists[0].id] : [],
                  );
                  setListLabel(contactsLists?.[0]?.label || "");
                } else {
                  const value = contactsLists?.filter(
                    (item) => item?.id === payload.list_id,
                  );
                  setSelectedList(value?.[0]?.id ? [value[0].id] : []);
                  setListLabel(value?.[0]?.label || "");
                }
                setContactList(contactsLists);
                setSequenceList(sequences);
                setActiveStep(activeStep + 1);
                setErrorMessage("");
              }
            },
            {
              onError: (error) => {
                if (error?.status === 409) {
                  setValidations({
                    name: error?.data?.message,
                    scheduleDate: "",
                  });
                }
              },
            },
          );
        } else {
          setValidations({
            name: nameValidated?.message,
          });
        }
      }

      if (currentStep.label === STRINGS.TIMINGS) {
        setEditTiming(false);
      }
      const noValueItems = currentStep.requiredFields?.filter(
        (item) => !item?.length,
      );
      if (noValueItems?.length) {
        if (currentStep.label !== STRINGS.CAMP_BASIC_DETAILS) {
          EventEmitter.emit(EMITTER_ERROR, MESSAGE_ALL_FIELD_REQUIRED);
        }
      } else if (currentStep.label === STRINGS.SELECT_SEQ_LIST) {
        handleStepActivityApi(async () => {
          const payload = { list_id: selectedList?.[0] };
          const response = await postRequest(
            navigate,
            campaignEndpoints.blockedContactList,
            payload,
          );
          setBlockedList(response?.data?.data || []);
          setActiveStep(activeStep + 1);
          setErrorMessage("");
        });
      } else if (currentStep.label !== STRINGS.CAMP_BASIC_DETAILS) {
        setActiveStep(activeStep + 1);
        setErrorMessage("");
      }
    } catch (error) {}
  };

  const sequenceName = findSortedValue(sequence?.[0], sequenceList);
  const listName = findSortedValue(selectedList?.[0], contactList);

  useEffect(() => {
    handleInitialLoadingApi(async () => {
      const response3 = await getRequest(navigate, campaignEndpoints.EspList);
      const espLists = response3?.data?.data?.map(function (item) {
        return {
          id: item?.esp,
          label: item.name,
          image: item.image,
          email: item.email ? item.email : "",
        };
      });
      // `?.map` above yields undefined when the response carries no
      // data.data (200 with an empty/!=expected body). espList must stay an
      // array - the derived effects below index straight into it.
      setEspList(espLists || []);
      if (espLists?.length > 0) {
        setSelectedEsps([espLists[0].id]);
        setItemData(espLists[0]);
        setSendVia(espLists[0].label);
      }

      setTimeZone([payload?.timezone || timezone_objChange?.[0]?.id]);
    });

    if (nextstep === 3) {
      setActiveStep(1);
    }
    if (nextstep === 5) {
      setActiveStep(2);
    }
    if (nextstep === 6) {
      setActiveStep(3);
    }
    if (nextstep === 7) {
      setActiveStep(4);
    }
  }, [navigate, nextstep]);

  // Single source of truth for connected mailboxes. Fetched ONCE on mount -
  // populates the email -> account_id lookup (/signature/list is
  // mailbox-scoped and needs it) AND the per-provider mailbox lists that
  // drive the Individual picker. Provider/mode switches never refetch this;
  // they just re-filter/re-read what's already in mailList/outList/zohoList.
  useEffect(() => {
    (async () => {
      try {
        const response = await getRequest(
          navigate,
          authEndpoints.getApiKeyDetails,
        );
        const inboxes = response?.data?.data?.inboxes || {};
        const map = {};
        [
          ...(inboxes.gmail || []),
          ...(inboxes.outlook || []),
          ...(inboxes.zoho || []),
        ].forEach((m) => {
          if (m?.email && m?._id) map[m.email] = m._id;
        });
        setAccountIdByEmail(map);

        // GMAIL SEND-AS ALIAS (2026-08-12): `alias` is DISPLAY-ONLY. `label`
        // stays the ACCOUNT email because it is also the selection VALUE the
        // create payload submits (from_email/from_emails) — the backend
        // resolves the alias itself at schedule time. Mailboxes without an
        // alias get null and render exactly as before.
        const toListItems = (arr, fallbackEsp) =>
          (arr || []).map((item, ind) => ({
            id: ind + 1,
            label: item.email,
            alias: item.send_as_alias || null,
            esp: Number(item.esp) || fallbackEsp,
          }));
        setMailList(toListItems(inboxes.gmail, 2));
        setOutList(toListItems(inboxes.outlook, 5));
        setZohoList(toListItems(inboxes.zoho, 8));
      } catch (err) {
        // Non-fatal: signatures will fall back to the user-level default.
      }
    })();
  }, [navigate]);

  // The list whose keys the step should be showing. Driven straight off the
  // Sequence & List pick, so choosing a different list there swaps the keys.
  const activeKeysListId = selectedList?.[0] || "";

  // Fetch the list's keys as soon as a list is chosen, not when the step is
  // reached, so the step is already populated by the time the user clicks Next.
  useEffect(() => {
    if (!activeKeysListId) {
      loadedKeysListIdRef.current = "";
      setListKeys([]);
      setSelectedCustomizationKeys([]);
      setListKeysError("");
      setListKeysContactCount(null);
      return;
    }
    // Same list already resolved: keep the cached keys AND the user's picks.
    if (loadedKeysListIdRef.current === activeKeysListId) return;

    let cancelled = false;
    (async () => {
      setListKeysLoading(true);
      setListKeysError("");
      try {
        const response = await getRequest(
          navigate,
          `${campaignEndpoints.contactListFields}?list_id=${encodeURIComponent(
            activeKeysListId,
          )}`,
        );
        if (cancelled) return;
        const keys = normaliseListKeys(response?.data?.data);
        loadedKeysListIdRef.current = activeKeysListId;
        setListKeys(keys);
        setListKeysContactCount(
          typeof response?.data?.total_contacts === "number"
            ? response.data.total_contacts
            : null,
        );
        // Everything on by default: the user narrows down rather than having
        // to tick each key before the step will let them past.
        setSelectedCustomizationKeys(keys.map((item) => item.key));
      } catch (err) {
        if (cancelled) return;
        // Leave the ref empty so Try again (or re-picking the list) refetches.
        loadedKeysListIdRef.current = "";
        setListKeys([]);
        setSelectedCustomizationKeys([]);
        setListKeysContactCount(null);
        setListKeysError(
          err?.data?.message || STRINGS.CUSTOMIZATION_KEYS_ERROR,
        );
      } finally {
        if (!cancelled) setListKeysLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeKeysListId, listKeysRetryToken, navigate]);

  const handleToggleCustomizationKey = useCallback((keyId) => {
    setSelectedCustomizationKeys((prev) =>
      prev.includes(keyId)
        ? prev.filter((id) => id !== keyId)
        : [...prev, keyId],
    );
  }, []);

  const handleSelectAllCustomizationKeys = useCallback(() => {
    setSelectedCustomizationKeys(listKeys.map((item) => item.key));
  }, [listKeys]);

  const handleClearCustomizationKeys = useCallback(() => {
    setSelectedCustomizationKeys([]);
  }, []);

  const handleRetryListKeys = useCallback(() => {
    setListKeysRetryToken((token) => token + 1);
  }, []);

  // True when at least one selected Send Via provider is a multi-mailbox
  // OAuth ESP (Gmail/Outlook/Zoho) - gates the Group/Individual picker UI.
  const isMultiEmailEsp = selectedEsps.some((esp) => isOAuthMailboxEsp(esp));

  // Keep the "Send via" display label in sync with the multi-select cards
  // (e.g. "Gmail, Zoho"). Derived instead of set-per-click so it can never
  // go stale when a card is toggled off.
  useEffect(() => {
    const labels = (espList || [])
      .filter((e) => selectedEsps.includes(e.id))
      .map((e) => e.label);
    setSendVia(labels.join(", "));
  }, [selectedEsps.join(","), espList]);

  // itemData only matters as the single-mailbox fallback for a non-OAuth ESP
  // (isMultiEmailEsp false). Keep it pointed at the right (only) selection so
  // it never shows a stale mailbox after a card is toggled off.
  useEffect(() => {
    if (!selectedEsps.length) {
      setItemData({});
    } else if (selectedEsps.length === 1 && !isOAuthMailboxEsp(selectedEsps[0])) {
      const found = (espList || []).find((e) => e.id === selectedEsps[0]);
      if (found) setItemData(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEsps.join(","), espList]);

  // Group mode is "active" when the user is on the group radio for a
  // multi-mailbox provider. Note this is driven by the RADIO, not by whether
  // groups exist - an empty groups list in group mode still keeps us here and
  // simply shows "No groups", instead of falling back to individual mailboxes.
  const groupsEnabled =
    !groupsLoading && senderMode === "group" && isMultiEmailEsp;

  // Effective members a group contributes (all members − excluded), each
  // carrying its OWN esp - never the campaign's selected esp. members[].esp
  // comes from the /emails detail endpoint; groupMemberEspByEmail (captured
  // from group/list) is a fallback if that endpoint hasn't echoed it back.
  const effectiveMembersForGroup = (groupId) => {
    const members = groupMembersById[groupId] || [];
    const excluded = excludedEmailsByGroupId[groupId] || new Set();
    return members
      .filter((m) => m?.email && !excluded.has(m.email))
      .map((m) => ({
        email: m.email,
        esp: Number(m.esp ?? groupMemberEspByEmail[m.email]),
      }));
  };
  // Back-compat helper: just the email list, used by the Preview breakdown.
  const effectiveEmailsForGroup = (groupId) =>
    effectiveMembersForGroup(groupId).map((m) => m.email);

  // Ordered, deduped {email, esp} across every selected group - order is
  // "group pick order, then that group's own member order", the closest
  // meaningful notion of "selection order" for group mode.
  const groupDrivenSenders = useMemo(() => {
    if (!groupsEnabled) return [];
    const seen = new Set();
    const result = [];
    selectedGroupIds.forEach((gid) => {
      effectiveMembersForGroup(gid).forEach((s) => {
        if (seen.has(s.email)) return;
        seen.add(s.email);
        result.push(s);
      });
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupsEnabled,
    selectedGroupIds.join("|"),
    Object.keys(groupMembersById).join("|"),
    Object.keys(excludedEmailsByGroupId)
      .map(
        (k) =>
          `${k}:${[...(excludedEmailsByGroupId[k] || new Set())].join(",")}`,
      )
      .join("|"),
    Object.keys(groupMemberEspByEmail).join("|"),
  ]);

  // For the Preview step: list each selected group with the emails it
  // contributes, so users can tell which mailbox came from which group.
  // Order matches selectedGroupIds (= the order the user picked the groups).
  const groupBreakdown = useMemo(() => {
    if (!groupsEnabled) return null;
    return selectedGroupIds
      .map((gid) => {
        const group = groups.find((g) => g._id === gid);
        if (!group) return null;
        return {
          _id: gid,
          group_name: group.group_name,
          emails: effectiveEmailsForGroup(gid),
        };
      })
      .filter((g) => g && g.emails.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupsEnabled,
    groups,
    selectedGroupIds.join("|"),
    Object.keys(groupMembersById).join("|"),
    Object.keys(excludedEmailsByGroupId)
      .map(
        (k) =>
          `${k}:${[...(excludedEmailsByGroupId[k] || new Set())].join(",")}`,
      )
      .join("|"),
  ]);

  // Unified sender list - {email, esp} in selection order, correct per-email
  // esp always attached at the source (never inferred/guessed downstream).
  // Group mode: groupDrivenSenders. Individual mode: standaloneSenders (click
  // order, across any mix of selected providers). Neither: single legacy
  // itemData mailbox.
  const selectedSenderInfos = groupsEnabled
    ? groupDrivenSenders
    : isMultiEmailEsp
      ? standaloneSenders
      : itemData?.email
        ? [{ email: itemData.email, esp: Number(itemData.id) }]
        : [];
  const selectedSenderEmails = selectedSenderInfos.map((s) => s.email);

  // ─── Signature gate ──────────────────────────────────────────────────
  // A campaign can't advance past Basic Details unless every selected sender
  // mailbox has at least one signature. `signatureListByEmail[email]` is the
  // fetched list ([] means confirmed-none); `signatureLoadingByEmail` flags an
  // in-flight fetch. Mirrors SenderSignaturePicker's "missing" state.
  const signaturesStillLoading = selectedSenderEmails.some(
    (email) => signatureLoadingByEmail[email],
  );
  const sendersMissingSignature = selectedSenderEmails.filter((email) => {
    const list = signatureListByEmail[email];
    return Array.isArray(list) && list.length === 0;
  });
  const hasSignatureGap = sendersMissingSignature.length > 0;

  // ─── Mailbox groups ─────────────────────────────────────────────────
  // Fetch once on mount; tolerate 4xx/network so flat picker still works.
  useEffect(() => {
    (async () => {
      try {
        setGroupsLoading(true);
        const response = await getRequest(
          navigate,
          CampaignSettingsEndpoints.mailboxGroupList,
        );
        const data = response?.data?.data;
        const list = Array.isArray(data) ? data : data?.groups || [];
        const mapped = list.map((g) => ({
          _id: g._id,
          group_name: g.group_name,
          member_count:
            typeof g.member_count === "number" ? g.member_count : 0,
        }));
        setGroups(mapped);
        // Capture each member's esp straight from group/list, as a fallback
        // for the per-group /emails detail endpoint below.
        const espMap = {};
        list.forEach((g) => {
          (g.members || []).forEach((m) => {
            if (m?.email && m?.esp != null) espMap[m.email] = Number(m.esp);
          });
        });
        setGroupMemberEspByEmail((prev) => ({ ...prev, ...espMap }));
        // First time only: default the radio to "group" when groups exist,
        // otherwise start on "standalone". A user switch afterwards sticks.
        if (!senderModeAutoSetRef.current) {
          senderModeAutoSetRef.current = true;
          setSenderMode(mapped.length > 0 ? "group" : "standalone");
        }
      } catch (err) {
        setGroups([]);
        if (!senderModeAutoSetRef.current) {
          senderModeAutoSetRef.current = true;
          setSenderMode("standalone");
        }
      } finally {
        setGroupsLoading(false);
      }
    })();
  }, [navigate]);

  // Lazy-load members for a group; cached after first hit. useCallback keeps a
  // stable-ish identity so the group handlers (and the memoized step that
  // receives them) only re-create when the cache actually changes. NOTE: the
  // "already cached" guard MUST read groupMembersById from the closure (a
  // setState updater runs during render, not synchronously here), so
  // groupMembersById is a real dependency.
  const fetchGroupMembers = useCallback(
    async (groupId) => {
      if (!groupId || groupMembersById[groupId]) return;
      setGroupMembersLoadingById((prev) => ({ ...prev, [groupId]: true }));
      try {
        const response = await getRequest(
          navigate,
          `${CampaignSettingsEndpoints.mailboxGroupEmails}/${groupId}/emails`,
        );
        const emails = response?.data?.data?.emails || [];
        setGroupMembersById((prev) => ({ ...prev, [groupId]: emails }));
      } catch (err) {
        setGroupMembersById((prev) => ({ ...prev, [groupId]: [] }));
      } finally {
        setGroupMembersLoadingById((prev) => ({ ...prev, [groupId]: false }));
      }
    },
    [navigate, groupMembersById],
  );

  // Clean handoff between the two radios. Individual mode owns
  // standaloneSenders; group mode is driven purely by groupDrivenSenders. On
  // a USER mode switch, wipe individual picks so returning to Individual
  // starts fresh instead of carrying over a stale mix.
  const handleSenderModeChange = useCallback((next) => {
    setSenderMode(next);
    setStandaloneSenders([]);
    if (next === "standalone") {
      // Leaving group mode: drop group selections so coming back is fresh.
      setSelectedGroupIds([]);
      setExcludedEmailsByGroupId({});
    }
  }, []);

  // Group toggle: select/clear the group. Selecting also clears prior excludes
  // so the user gets every member fresh; deselecting wipes its excluded set.
  const handleToggleGroup = useCallback(
    (groupId) => {
      setSelectedGroupIds((prev) => {
        if (prev.includes(groupId)) {
          setExcludedEmailsByGroupId((prevEx) => {
            const next = { ...prevEx };
            delete next[groupId];
            return next;
          });
          return prev.filter((g) => g !== groupId);
        }
        // Pre-fetch members if we haven't yet; selection state is fine to set
        // immediately because the union effect re-runs when members arrive.
        fetchGroupMembers(groupId);
        setExcludedEmailsByGroupId((prevEx) => {
          const next = { ...prevEx };
          delete next[groupId];
          return next;
        });
        return [...prev, groupId];
      });
    },
    [fetchGroupMembers],
  );

  // Per-email toggle inside View More. Only meaningful when the group is on.
  // The "is this group selected" guard reads selectedGroupIds from the closure
  // (a setState updater runs during render, not synchronously here), so
  // selectedGroupIds is a real dependency of this callback.
  const handleToggleGroupMember = useCallback(
    (groupId, email) => {
      if (!selectedGroupIds.includes(groupId)) return;
      setExcludedEmailsByGroupId((prev) => {
        const current = new Set(prev[groupId] || new Set());
        if (current.has(email)) current.delete(email);
        else current.add(email);
        return { ...prev, [groupId]: current };
      });
    },
    [selectedGroupIds],
  );

  // Expand-on-demand for View More.
  const handleExpandGroup = useCallback(
    (groupId) => {
      fetchGroupMembers(groupId);
    },
    [fetchGroupMembers],
  );

  // payload override > mailbox default > first available.
  const pickDefaultSignature = (sigItems) => {
    let chosen = null;
    if (payload?.other_signature_id) {
      chosen = sigItems.find((s) => s.id === payload.other_signature_id);
    }
    if (!chosen) chosen = sigItems.find((s) => s.is_default);
    if (!chosen) chosen = sigItems[0];
    return chosen || null;
  };

  // Lazy per-email signature fetch that RETURNS the normalised list (and also
  // caches it in state). Returns [] on failure/none. Skips the network call if
  // already cached. The signature gate awaits this so it never races the effect.
  const fetchSignatureListForEmail = async (email) => {
    if (!email) return [];
    // Fast path: already resolved and cached.
    if (Array.isArray(signatureListByEmail[email])) {
      return signatureListByEmail[email];
    }
    // De-dupe: an identical fetch is already in flight — reuse its promise so
    // the prefetch effect and the Next-button gate don't both hit the network.
    if (signaturePromisesByEmail.current[email]) {
      return signaturePromisesByEmail.current[email];
    }

    const doFetch = (async () => {
      setSignatureLoadingByEmail((prev) => ({ ...prev, [email]: true }));
      try {
        const accountId = accountIdByEmail[email];
        const url = accountId
          ? `${CampaignSettingsEndpoints.signatureList}?account_id=${accountId}`
          : CampaignSettingsEndpoints.signatureList;
        const response = await getRequest(navigate, url);
        const data = response?.data?.data;

        // /signature/list returns a flat array when account_id is provided,
        // and a map keyed by account_id when it isn't. Normalise both.
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && typeof data === "object") {
          list = Object.values(data).flatMap(
            (entry) => entry?.signatures || [],
          );
        }

        const sigItems = list.map((item) => ({
          id: item?._id,
          label: item?.signature_name || item?.email || "",
          signature: item?.signature,
          is_default: !!item?.is_default,
        }));

        setSignatureListByEmail((prev) => ({ ...prev, [email]: sigItems }));
        return sigItems;
      } catch (err) {
        setSignatureListByEmail((prev) => ({ ...prev, [email]: [] }));
        return [];
      } finally {
        setSignatureLoadingByEmail((prev) => ({ ...prev, [email]: false }));
        delete signaturePromisesByEmail.current[email];
      }
    })();

    signaturePromisesByEmail.current[email] = doFetch;
    return doFetch;
  };

  // Fire-and-forget wrapper kept for the lazy prefetch effect / callers that
  // don't need the returned list.
  const fetchSignaturesForEmail = async (email) => {
    if (!email) return;
    if (signatureListByEmail[email]) return;
    await fetchSignatureListForEmail(email);
  };

  // Fetch on demand for every currently-selected email that isn't cached yet.
  useEffect(() => {
    selectedSenderEmails.forEach((email) => {
      if (email && !signatureListByEmail[email]) {
        fetchSignaturesForEmail(email);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSenderEmails.join("|"),
    Object.keys(accountIdByEmail).join("|"),
  ]);

  // Pre-fill default signature whenever a selected email has its list cached
  // but no chosen signature yet. Runs on first fetch AND on re-check.
  useEffect(() => {
    selectedSenderEmails.forEach((email) => {
      if (!email) return;
      if (signaturesByEmail[email]) return;
      const list = signatureListByEmail[email];
      if (!Array.isArray(list) || list.length === 0) return;
      const chosen = pickDefaultSignature(list);
      if (chosen) {
        setSignaturesByEmail((prev) => ({ ...prev, [email]: chosen }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSenderEmails.join("|"),
    Object.keys(signatureListByEmail).join("|"),
  ]);

  // Drop signature entries whose email is no longer selected.
  useEffect(() => {
    setSignaturesByEmail((prev) => {
      const next = {};
      selectedSenderEmails.forEach((email) => {
        if (prev[email]) next[email] = prev[email];
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSenderEmails.join("|")]);

  // Keep the legacy single-signature state in sync with the first selected
  // email so the preview step and payload (other_signature_id) keep working
  // until the backend supports a per-email signature map.
  const firstSignature = selectedSenderEmails
    .map((e) => signaturesByEmail[e])
    .find(Boolean);
  useEffect(() => {
    if (firstSignature) {
      setIdSignState([firstSignature.id]);
      setSignState([firstSignature.label]);
    } else {
      setIdSignState([]);
      setSignState([]);
    }
  }, [firstSignature?.id, firstSignature?.label]);

  // Individual mode toggle. `esp` comes straight from the row that was
  // clicked (each entry in the combined available-mailboxes list already
  // carries its own real esp - Gmail rows are esp 2, Zoho rows are esp 8,
  // etc.), so a mailbox's tag can never bleed into the wrong provider even
  // when multiple providers are selected at once. Functional setState so
  // bulk callers (Select all, Clear) invoking this in a tight forEach see
  // each prior update instead of a stale closure.
  const handleToggleSenderEmail = useCallback((email, esp) => {
    setStandaloneSenders((prev) =>
      prev.some((s) => s.email === email)
        ? prev.filter((s) => s.email !== email)
        : [...prev, { email, esp: Number(esp) }],
    );
  }, []);

  // Deselecting a Send Via provider drops its mailboxes from the Individual
  // picks too. Without this, a mailbox picked while its provider was selected
  // stays checked (and still counts toward from_emails) even after the user
  // removes that provider card - and silently reappears if they reselect it.
  useEffect(() => {
    setStandaloneSenders((prev) =>
      prev.filter((s) => selectedEsps.includes(s.esp)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEsps.join(",")]);

  useEffect(() => {
    if (
      icpPersonaEnabled &&
      companyPersonas.length === 0 &&
      personPersonas.length === 0
    ) {
      setPersonasLoading(true);
      getRequest(navigate, campaignEndpoints.campaignPersonas)
        .then((response) => {
          const data = response?.data?.data;
          if (data) {
            const companyItems = (data.company || []).map((item) => ({
              id: item.persona_id,
              label: item.name,
            }));
            const personItems = (data.person || []).map((item) => ({
              id: item.persona_id,
              label: item.name,
            }));
            setCompanyPersonas(companyItems);
            setPersonPersonas(personItems);
          }
        })
        .catch((err) => {
          console.error("Error fetching personas", err);
        })
        .finally(() => {
          setPersonasLoading(false);
        });
    }
  }, [icpPersonaEnabled]);

  const handleGetData = useCallback((newTimings) => {
    setGetTimings(newTimings);
    // console.log("NWTIMING",newTimings)
    const messageLen = newTimings?.filter((item) => item?.message?.length > 0);
    if (messageLen?.length > 0) {
      setError(true);
    } else {
      setError(false);
    }
  }, []);

  const handleEligibleHours = async () => {
    const date = new Date();
    const currentDate = `${date.getFullYear()}-${
      date.getMonth() + 1 <= 9 ? "0" : ""
    }${date.getMonth() + 1}-${date.getDate() <= 9 ? "0" : ""}${date.getDate()}`;
    const scheduledDate = currentDate;
    const val = createTimingsForPayload(getTimings);
    const dateArray = scheduledDate?.split("-");
    // DST-aware offset for selected tz on today's date.
    const offset = offsetForTimezoneOnDate(timezone?.[0], date);
    const newScheduleDate = `${parseInt(dateArray[2])}-${parseInt(
      dateArray[1],
    )}-${parseInt(dateArray[0])}`;
    const emailVal = selectedSenderInfos?.[0]?.email || "";
    const senderEspVal =
      selectedSenderInfos?.[0]?.esp || Number(selectedEsps?.[0]) || 0;

    const payload = {
      timings: val,
      name: name,
      sequence_id: sequence?.[0] || "",
      list_id: selectedList?.[0] || "",
      scheduled_date: newScheduleDate,
      from_email: emailVal,
      timezone: timezone?.[0],
      offset,
      sender_esp: senderEspVal,
    };

    handleEligibleApi(
      () => postRequest(navigate, campaignEndpoints.eligibleHours, payload),
      {
        onSuccess: (response) => {
          if (response?.status === 200) {
            setEditTiming(false);
          }
        },
        onError: (err) => {
          if (err?.status === 403) {
            EventEmitter.emit(
              EMITTER_SUCCESS,
              "We've extended the timeframe to ensure the success of the campaign.",
            );
            setEditTiming(false);
          }
        },
      },
    );
  };

  const handleEditTimings = useCallback(() => {
    setEditTiming((prev) => !prev);
  }, []);

  // `type` is the espConnectionList button id (1 google, 2 outlook, 3 zoho),
  // NOT the ESP code. Route each to its OAuth connect helper.
  const handleConnectMail = useCallback((type) => {
    if (type === 1) {
      handleGoogleAuth();
    } else if (type === 3) {
      handleZohoAuth();
    } else {
      handleMicrosoftAuthInitialize();
    }
  }, []);

  const searchedTimezoneList = useCallback((event) => {
    let all_timezone = [...TIMEZONE_LIST];

    if (event?.target?.value) {
      let filtered_timezone = all_timezone?.filter((item) =>
        item?.label
          ?.toLowerCase()
          ?.includes(event?.target?.value?.toLowerCase()),
      );
      return setTimezoneList(filtered_timezone?.slice(0, 100));
    } else {
      return setTimezoneList(all_timezone);
    }
  }, []);

  const step1 = (
    <BasicDetailsStep
      classes={classes}
      name={name}
      setName={setName}
      validations={validations}
      unsubscribeEnabled={unsubscribeEnabled}
      setUnsubscribeEnabled={setUnsubscribeEnabled}
      trackingEnabled={trackingEnabled}
      setTrackingEnabled={setTrackingEnabled}
      loading={initialLoading}
      espList={espList}
      selectedEsps={selectedEsps}
      setSelectedEsps={setSelectedEsps}
      setItemData={setItemData}
      itemData={itemData}
      espConnectionList={espConnectionList}
      handleConnectMail={handleConnectMail}
      outList={outList}
      mailList={mailList}
      zohoList={zohoList}
      signaturesByEmail={signaturesByEmail}
      signatureListByEmail={signatureListByEmail}
      signatureLoadingByEmail={signatureLoadingByEmail}
      onToggleSenderEmail={handleToggleSenderEmail}
      selectedSenderEmails={selectedSenderEmails}
      senderMode={senderMode}
      setSenderMode={handleSenderModeChange}
      groupsEnabled={groupsEnabled}
      groups={groups}
      groupsLoading={groupsLoading}
      selectedGroupIds={selectedGroupIds}
      groupMembersById={groupMembersById}
      groupMembersLoadingById={groupMembersLoadingById}
      excludedEmailsByGroupId={excludedEmailsByGroupId}
      onToggleGroup={handleToggleGroup}
      onExpandGroup={handleExpandGroup}
      onToggleGroupMember={handleToggleGroupMember}
    />
  );

  const step2 = (
    <SelectSequenceStep
      classes={classes}
      sequenceType={sequenceType}
      handleSequenceTypeChange={handleSequenceTypeChange}
      sequenceList={sequenceList}
      sequenceLabel={sequenceLabel}
      setSequenceLabel={setSequenceLabel}
      sequence={sequence}
      setSequence={setSequence}
      contactList={contactList}
      listLabel={listLabel}
      setListLabel={setListLabel}
      selectedList={selectedList}
      setSelectedList={setSelectedList}
      icpPersonaEnabled={icpPersonaEnabled}
      setIcpPersonaEnabled={setIcpPersonaEnabled}
      companyPersonas={companyPersonas}
      personPersonas={personPersonas}
      companyPersonaId={companyPersonaId}
      setCompanyPersonaId={setCompanyPersonaId}
      companyPersonaLabel={companyPersonaLabel}
      setCompanyPersonaLabel={setCompanyPersonaLabel}
      personPersonaId={personPersonaId}
      setPersonPersonaId={setPersonPersonaId}
      personPersonaLabel={personPersonaLabel}
      setPersonPersonaLabel={setPersonPersonaLabel}
      personasLoading={personasLoading}
      hideSequenceTypeSelector={
        isDemoTenant() || isRestrictedTenant() || isTenantPromptOnlyTenant()
      }
    />
  );

  const stepKeys = (
    <CustomizationKeysStep
      classes={classes}
      listKeys={listKeys}
      selectedKeys={selectedCustomizationKeys}
      onToggleKey={handleToggleCustomizationKey}
      onSelectAllKeys={handleSelectAllCustomizationKeys}
      onClearKeys={handleClearCustomizationKeys}
      loading={listKeysLoading}
      errorMessage={listKeysError}
      onRetry={handleRetryListKeys}
      hasList={!!activeKeysListId}
      contactCount={listKeysContactCount}
    />
  );

  const step3 = (
    <SkippedContactsStep classes={classes} blockedList={blockedList} />
  );

  const step4 = (
    <ScheduleTimingsStep
      classes={classes}
      timezoneList={timezoneList}
      timezone={timezone}
      setTimeZone={setTimeZone}
      searchedTimezoneList={searchedTimezoneList}
      scheduleDate={scheduleDate}
      setScheduleDate={setScheduleDate}
      editTiming={editTiming}
      getTimings={getTimings}
      handleGetData={handleGetData}
      setGetTimings={setGetTimings}
      error={error}
      handleEligibleHours={handleEligibleHours}
      isEligibleLoad={isEligibleLoad}
      handleEditTimings={handleEditTimings}
      sequenceType={sequenceType}
    />
  );

  const step5 = (
    <CampaignPreviewStep
      classes={classes}
      name={name}
      sendVia={sendVia}
      isMultiEmailEsp={isMultiEmailEsp}
      itemData={itemData}
      sequenceName={sequenceName}
      listName={listName}
      blockedList={blockedList}
      scheduleDate={scheduleDate}
      timezone={timezone}
      getTimings={getTimings}
      millisecondsToDateStringWithoutNumber={
        millisecondsToDateStringWithoutNumber
      }
      convertData={convertData}
      sequenceType={sequenceType}
      icpPersonaEnabled={icpPersonaEnabled}
      companyPersonaLabel={companyPersonaLabel}
      personPersonaLabel={personPersonaLabel}
      selectedSenderEmails={selectedSenderEmails}
      signaturesByEmail={signaturesByEmail}
      groupBreakdown={groupBreakdown}
      listKeys={listKeys}
      selectedCustomizationKeys={selectedCustomizationKeys}
      onJumpToStep={(stepIndex) => setActiveStep(stepIndex)}
    />
  );

  const steps = [
    {
      label: STRINGS.CAMP_BASIC_DETAILS,
      component: step1,
      requiredFields: [
        name?.trim(),
        espList,
        selectedSenderEmails,
      ],
    },
    {
      label: STRINGS.SELECT_SEQ_LIST,
      component: step2,
      requiredFields: [sequence, selectedList],
    },
    {
      label: STRINGS.CUSTOMIZATION_KEYS,
      component: stepKeys,
      // Only gate on a selection once the list actually returned keys. If the
      // endpoint is unreachable or the list exposes none, the step stays
      // informational and must never block campaign creation.
      requiredFields: listKeys.length ? [selectedCustomizationKeys] : [],
    },
    // { label: STRINGS.SKIPPED_CONTACTS, component: step3, requiredFields: [] },
    {
      label: STRINGS.TIMINGS,
      component: step4,
      requiredFields:
        sequenceType === "automated" || sequenceType === "tenantPrompt"
          ? []
          : [timezone],
    },
    {
      label: STRINGS.PREVIEW,
      component: step5,
      requiredFields:
        sequenceType === "automated" || sequenceType === "tenantPrompt"
          ? []
          : [scheduleDate],
    },
  ].filter((step) => {
    if (step.label === STRINGS.TIMINGS) {
      return sequenceType === "manual";
    }
    return true;
  });

  // The signature notice on Next only applies while on the Basic Details step
  // (where the sender mailboxes are chosen). It no longer disables the button -
  // signature-less mailboxes are dropped from the senders on Next instead.
  const isBasicDetailsStep =
    steps?.[activeStep]?.label === STRINGS.CAMP_BASIC_DETAILS;

  // Hold Next while the list's keys are still in flight, so nobody skips the
  // step before it has anything to select and creates a campaign with an empty
  // customization_keys.
  const isCustomizationKeysStep =
    steps?.[activeStep]?.label === STRINGS.CUSTOMIZATION_KEYS;
  const isWaitingOnListKeys = isCustomizationKeysStep && listKeysLoading;

  // isEnableList means "the Sequence & List step has nothing to pick". It is
  // raised while leaving Basic Details and can only be cleared from inside that
  // same Next handler, so left unscoped it disables Next on EVERY step - Back
  // then leaves the user with no way to clear it at all.
  const isSequenceListStep =
    steps?.[activeStep]?.label === STRINGS.SELECT_SEQ_LIST;

  const modalHeaderData = {
    heading: {
      id: STRINGS.HEAD,
      label: STRINGS.CREATE_CAMPAIGN,
    },
    description: {
      id: STRINGS.HEAD,
      label: `Follow ${steps.length} simple Steps to create your campaign`,
    },
  };

  const modalFooterLeftData = (
    <div>
      <Button
        label={STRINGS.CANCEL}
        variant={"ghost"}
        size={"sm36"}
        onClick={() => {
          setOpen(false);
          setPayload(false);
        }}
      />
    </div>
  );

  const modalFooterRightData = (
    <div>
      <Box className={classes.campBtnStyle}>
        {activeStep === 0 ? (
          <Box></Box>
        ) : (
          <Box className={classes.backBtnContainer}>
            <Button
              label={STRINGS.BACK}
              variant={"ghost"}
              size={"sm36"}
              onClick={() => {
                setActiveStep(activeStep - 1);
                setEditTiming(false);
              }}
            />
          </Box>
        )}
        {activeStep === steps.length - 1 ? (
          <Button
            label={
              sequenceType === "automated" || sequenceType === "tenantPrompt"
                ? STRINGS.CREATE_CAMPAIGN
                : STRINGS.START_CAMP
            }
            size={"sm36"}
            variant={"primary"}
            onClick={() => handleCreateCampaign(true)}
            isLoading={creating}
          />
        ) : null}
        <>
          {activeStep === steps.length - 1 &&
          (sequenceType === "automated" || sequenceType === "tenantPrompt") ? (
            <Box></Box>
          ) : (
            <CustomTooltip
              title={
                isBasicDetailsStep && hasSignatureGap && !signaturesStillLoading
                  ? STRINGS.CAMP_SIGNATURE_GATE_TOOLTIP
                  : isBasicDetailsStep && signaturesStillLoading
                  ? STRINGS.CAMP_SIGNATURE_CHECKING
                  : ""
              }
              variant="top"
            >
              <Box>
                <Button
                  label={
                    activeStep === steps.length - 1
                      ? STRINGS.SCHEDULE_CAMPAIGN
                      : STRINGS.NEXT
                  }
                  size={"sm36"}
                  variant={"primary"}
                  onClick={() =>
                    activeStep === steps.length - 1
                      ? handleCreateCampaign()
                      : handleStepAddition()
                  }
                  isLoading={creating1 || stepActivity}
                  disabled={
                    isNextStepDisabled() ||
                    editTiming ||
                    (isEnableList && isSequenceListStep) ||
                    isWaitingOnListKeys
                  }
                />
              </Box>
            </CustomTooltip>
          )}
        </>
      </Box>
    </div>
  );

  const modalBodyData = (
    <div>
      <Box>
        <Stepper
          className={classes.heightStyle}
          activeStep={activeStep}
          orientation="vertical"
        >
          <div className={classes.stepper}>
            <Box className={classes.stepsContainer}>
              <div className={classes.stepperdiv}>
                {steps.map((step, index) => (
                  <div key={step.label} className={classes.marginDiv}>
                    <div className={classes.steplab}>
                      <div className={classes.steplabs}>
                        <TypographyInline
                          size={"sm"}
                          isEllipses={true}
                          color={
                            activeStep === index ? GREY.PRIMARY : GREY.SECONDARY
                          }
                          fontWeight={
                            activeStep === index
                              ? fontWeight.MEDIUM
                              : fontWeight.REGULAR
                          }
                          label={step.label}
                        />
                      </div>
                      <div className={classes.divv}>
                        <span>
                          <div>
                            {activeStep === index ? (
                              <Image
                                src={ACTIVE_IMG}
                                alt={STRINGS.SMALL_ACTIVE}
                                className={classes.MarginImage}
                              />
                            ) : activeStep > index ? (
                              <Image
                                src={DONE_IMG}
                                alt={STRINGS.SMALL_DONE}
                                className={classes.MarginImage}
                              />
                            ) : (
                              <Image
                                src={UNSELECTED_IMG}
                                alt={STRINGS.UNSEL}
                                className={classes.MarginImage}
                              />
                            )}
                          </div>
                        </span>
                        {!(steps.length - 1 === index) && (
                          <div className={classes.vertHt}></div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Box className={classes.containEachStep} data-tour="campaign-modal-right-panel">
                {steps?.[activeStep]?.component &&
                  React.cloneElement(steps?.[activeStep]?.component, {
                    currentStep: activeStep + 1,
                    totalSteps: steps.length,
                  })}
              </Box>
              {/* Active Component */}

              {errorMessage ? <ErrorMessage message={errorMessage} /> : null}
              {/* Active Component */}
            </Box>
          </div>
        </Stepper>
      </Box>
    </div>
  );

  return (
    <CustomModal
      remove_padding={true}
      className={classes.modalConiner}
      open={open}
      close={() => {
        setOpen(false);
        setPayload(false);
      }}
      header={modalHeaderData}
      children={modalBodyData}
      footer={{
        left: modalFooterLeftData,
        right: modalFooterRightData,
      }}
      width={"63vw"}
    />
  );
};

export default CreateCampaignModal;
